<template>
  <div class="hw-page" :class="fontClass">
    <div class="hw-inner">
      <Banner
        :stages="globalStages"
        :can-play="canPlay"
        :can-claim="canClaim"
        @receive="handleReceiveGlobal"
        @preview="handlePreviewGlobal"
      />
      <UserLogin @login="showLogin = true" @logout="onLogout" />
      <Contribute
        :tasks="displayTasks"
        :records="rewardRecords"
        :can-play="canPlay"
        :logged-in="isLoggedIn"
        :available-count="availableStrikeCount"
        @strike="handleStrike"
        @share="openShareDialog"
        @receive-task="handleReceiveTask"
        @open-records="toggleRecords"
      />
      <!-- <header class="hw-top">
        <div>
          <p class="hw-kicker">Halloween</p>
          <h1>{{ t('halloween.title') }}</h1>
          <p class="hw-phase">{{ phaseText }}</p>
        </div>
      </header> -->

      <!-- <section class="hw-card hw-hero">
        <p>{{ t('halloween.intro') }}</p>
        <p v-if="ghostDefeated" class="hw-badge">{{ t('halloween.ghostDefeated') }}</p>
      </section> -->

      <Milestone
        :milestones="displayMilestones"
        :can-play="canPlay"
        :activityInfo="activityInfo"
        @receive="handleReceiveMilestone"
      />
      <Footer />
    </div>
    <FloatTools></FloatTools>

    <LoginDialog v-model="showLogin"></LoginDialog>
    <ShareDialog v-model="showShare" @shareSuccess="handleShareSuccess" />
    <ActTimeDialog
      v-model="showActTime"
      :type="actTimeType"
      :start-ms="activityStartMs"
      :server-ms="serverMs"
      @countdown-end="onActCountdownEnd"
    />
    <RewardDialog
      :model-value="rewardPopup.length > 0"
      :rewards="rewardPopup"
      @update:model-value="rewardPopup = []"
    />
    <RewardDialog
      :model-value="stagePreview.length > 0"
      :rewards="stagePreview"
      :show-title="false"
      @update:model-value="stagePreview = []"
    />
    <StrikeRewardDialog
      :model-value="strikePopup > 0"
      :count="strikePopup"
      @update:model-value="strikePopup = 0"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import ta from 'thinkingdata-browser'
import HwApi from '@/config/hwActApi'
import { toastService } from '@/utils/toastService'
import { halloweenFontClass } from './assets/fonts/fontClass'
import './assets/fonts/font.css'

import Banner from './components/Banner'
import UserLogin from './components/UserLogin'
import Contribute from './components/Contribute'
import Milestone from './components/Milestone'
import Footer from './components/Footer/index.vue'
import FloatTools from './components/FloatTools/index.vue'
import LoginDialog from './components/Dialog/LoginDialog/index.vue'
import ShareDialog from './components/Dialog/ShareDialog/index.vue'
import ActTimeDialog from './components/Dialog/ActTimeDialog/index.vue'
import RewardDialog from './components/Dialog/RewardDialog/index.vue'
import StrikeRewardDialog from './components/Dialog/StrikeRewardDialog/index.vue'


const SHARE_WAIT_MS = 1000
const FIRST_LOGIN_KEY = 'halloween_first_login_received'

const store = useStore()
const route = useRoute()
const router = useRouter()
const { t, te, locale } = useI18n()
const fontClass = computed(() => halloweenFontClass(locale.value))

const showLogin = ref(false)
const showShare = ref(false)
const showActTime = ref(false)
const actTimeType = ref('not-start') // not-start | ended
const guestStages = ref([])
const taskList = ref([])
const milestoneList = ref([])
const rewardRecords = ref([])
const rewardPopup = ref([])
const stagePreview = ref([])
const strikePopup = ref(0)
const sharing = ref(false)
const handledFirstLogin = new Set()
let shareTimer = null

const parseActTime = (time) => {
  if (!time) return 0
  const value = String(time).trim()
  if (!value) return 0
  if (/^\d+$/.test(value)) {
    const num = Number(value)
    return num > 1e12 ? num : num * 1000
  }
  const normalized = value.replace(' ', 'T')
  const hasTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalized)
  const ts = Date.parse(hasTimezone ? normalized : `${normalized}+08:00`)
  return Number.isFinite(ts) ? ts : 0
}

const actUserInfo = computed(() => store.state.actUserInfo || {})
const isLoggedIn = computed(() => !!actUserInfo.value?.token)
const activityInfo = computed(() => store.state.hwAct?.activityInfo)
const commonConfig = computed(() => store.state.hwAct?.commonConfig)
const activityPhase = computed(
  () => activityInfo.value?.activityPhase || commonConfig.value?.activityPhase || '',
)
const canPlay = computed(() => activityPhase.value === 'play')
// 领奖窗：参与期 + view_only（PlayEnd～RewardEnd/PageClose）
const canClaim = computed(
  () => activityPhase.value === 'play' || activityPhase.value === 'view_only',
)
const availableStrikeCount = computed(() => activityInfo.value?.availableStrikeCount || 0)
const activityStartMs = computed(() => parseActTime(commonConfig.value?.activityStartTime))
const serverMs = computed(() => parseActTime(commonConfig.value?.serverTime))
// const ghostDefeated = computed(() => !!activityInfo.value?.ghostDefeated)
const phaseText = computed(() => {
  const key = `halloween.phase.${activityPhase.value}`
  return te(key) ? t(key) : activityPhase.value
})

const syncActTimeDialog = () => {
  const phase = activityPhase.value
  if (!phase) {
    showActTime.value = false
    return
  }
  if (phase === 'not_start') {
    actTimeType.value = 'not-start'
    showActTime.value = true
    return
  }
  // 仅关页阶段弹结束窗；view_only 仍可浏览/领奖，不弹
  if (phase === 'closed') {
    actTimeType.value = 'ended'
    showActTime.value = true
    return
  }
  showActTime.value = false
}

const onActCountdownEnd = async () => {
  showActTime.value = false
  await loadCommon()
  if (isLoggedIn.value) {
    await initData()
  }
  syncActTimeDialog()
}

const globalStages = computed(() => {
  if (isLoggedIn.value && activityInfo.value?.globalStages?.length) {
    return activityInfo.value.globalStages
  }
  if (guestStages.value.length) return guestStages.value
  return commonConfig.value?.globalStages || []
})

const displayTasks = computed(() => {
  if (isLoggedIn.value && taskList.value.length) return taskList.value
  const map = commonConfig.value?.taskStrikeCountMap || {}
  return Object.keys(map).map((taskType) => ({
    taskType,
    progress: 0,
    target: 1,
    strikeCount: map[taskType],
    status: 0,
    isDaily: String(taskType).includes('daily'),
  }))
})

const displayMilestones = computed(() => {
  const commonList = commonConfig.value?.milestoneList || []
  if (!isLoggedIn.value || !milestoneList.value.length) return commonList
  const commonByCode = new Map(commonList.map((item) => [item.rewardCode, item]))
  return milestoneList.value.map((item) => {
    const commonItem = commonByCode.get(item.rewardCode) || {}
    return {
      ...commonItem,
      ...item,
      rewardItems: item.rewardItems?.length ? item.rewardItems : commonItem.rewardItems || [],
    }
  })
})

const accountId = () =>
  String(
    actUserInfo.value?.cpUserId ||
      actUserInfo.value?.uid ||
      actUserInfo.value?.uin ||
      '',
  )

const requireLogin = () => {
  if (isLoggedIn.value) return true
  showLogin.value = true
  return false
}

const requirePlay = () => {
  if (!requireLogin()) return false
  if (!canPlay.value) {
    toastService.show(phaseText.value || t('halloween.phase.view_only'))
    return false
  }
  return true
}

const requireClaim = () => {
  if (!requireLogin()) return false
  if (!canClaim.value) {
    toastService.show(phaseText.value || t('halloween.phase.closed'))
    return false
  }
  return true
}

const showRewards = (res) => {
  const items = res?.rewardItems || []
  if (items.length) rewardPopup.value = items
}

const loadCommon = async () => {
  try {
    await store.dispatch('hwAct/fetchCommonConfig')
  } catch (e) {
    console.error(e)
  }
}

const loadGlobalProgress = async () => {
  try {
    const res = await HwApi.getGlobalProgress({})
    guestStages.value = res?.globalStages || []
  } catch (e) {
    console.error(e)
  }
}

const loadActivity = async () => {
  if (!isLoggedIn.value) return
  await store.dispatch('hwAct/fetchActivityInfo')
}

const loadTasks = async () => {
  if (!isLoggedIn.value) return
  const res = await HwApi.getTaskList({})
  taskList.value = res?.taskList || []
}

const loadMilestones = async () => {
  if (!isLoggedIn.value) return
  const res = await HwApi.getMilestoneList({})
  milestoneList.value = res?.milestoneList || []
}

const loadRecords = async () => {
  if (!isLoggedIn.value) return
  const res = await HwApi.getRewardRecord({})
  rewardRecords.value = res?.recordList || []
}

const initData = async () => {
  await loadActivity()
  await Promise.all([loadTasks(), loadMilestones(), loadRecords()])
}

// 重新进入页面（切回前台）时刷新配置与业务数据
const refreshPageData = async () => {
  await loadCommon()
  await loadGlobalProgress()
  syncActTimeDialog()
  if (isLoggedIn.value) {
    await initData()
  }
}

const handleVisibilityChange = () => {
  if (!document.hidden) {
    refreshPageData()
  }
}

const readFirstLoginCache = () => {
  try {
    return JSON.parse(localStorage.getItem(FIRST_LOGIN_KEY) || '{}')
  } catch (e) {
    return {}
  }
}

const markFirstLoginCached = (id) => {
  const cache = readFirstLoginCache()
  cache[id] = 1
  localStorage.setItem(FIRST_LOGIN_KEY, JSON.stringify(cache))
  handledFirstLogin.add(id)
}

const tryAutoClaimFirstLogin = async () => {
  const id = accountId()
  if (!isLoggedIn.value || !id || !canClaim.value) return
  if (handledFirstLogin.has(id)) return
  // 以服务端领取态为准。本地缓存只在服务端已领取时写入，避免带登录态进页时被旧缓存跳过。
  if (activityInfo.value?.firstLoginRewardReceived) {
    markFirstLoginCached(id)
    return
  }
  try {
    const res = await HwApi.receiveFirstLoginReward({})
    markFirstLoginCached(id)
    showRewards(res)
    await loadActivity()
  } catch (e) {
    if (Number(e?.code) === 2801) {
      markFirstLoginCached(id)
    }
    console.error(e)
  }
}

const initLogin = async () => {
  const tokenHash = route.query?.tokenHash
  const uid = route.query?.uid
  if (!tokenHash || !uid) return
  router.replace({
    query: {
      ...route.query,
      uid: undefined,
      tokenHash: undefined,
    },
  })
  await HwApi.gameTokenLogin({ uid, tokenHash })
}

const onLogout = () => {
  store.commit('SET_ACT_USER_INFO', {})
  store.commit('hwAct/SET_ACTIVITY_INFO', null)
  taskList.value = []
  milestoneList.value = []
  rewardRecords.value = []
  loadGlobalProgress()
}

const handleStrike = async () => {
  if (!requirePlay()) return
  const res = await HwApi.strike({})
  toastService.show(`${t('halloween.strike')} x${res?.consumeCount || 0}`)
  await initData()
}

const SHARE_TASK = 'hw_weekly_share'

const openShareDialog = () => {
  showShare.value = true
}

const handleShareSuccess = () => {
  toastService.show(t('halloween.shareOk'))
  if (!isLoggedIn.value || !canPlay.value || sharing.value) return
  const shareTask = displayTasks.value.find((task) => task.taskType === SHARE_TASK)
  if (!shareTask || Number(shareTask.status) !== 0) return
  sharing.value = true
  shareTimer = setTimeout(async () => {
    try {
      await HwApi.share({})
      await loadTasks()
    } catch (e) {
      console.error(e)
    } finally {
      sharing.value = false
    }
  }, SHARE_WAIT_MS)
}

const handleReceiveTask = async (task) => {
  if (!requirePlay()) return
  const res = await HwApi.receiveTaskReward({ taskType: task.taskType })
  strikePopup.value = Number(res?.strikeCount || task.strikeCount || 0)
  await initData()
}

const handlePreviewGlobal = (stage) => {
  stagePreview.value = stage?.rewardItems || []
}

const handleReceiveGlobal = async (stage) => {
  if (!requireClaim()) return
  const res = await HwApi.receiveGlobalReward({ stage: stage.stage })
  showRewards(res)
  await initData()
}

const handleReceiveMilestone = async (item) => {
  if (!requireClaim()) return
  const res = await HwApi.receiveMilestone({ rewardCode: item.rewardCode })
  showRewards(res)
  await loadMilestones()
}

const toggleRecords = async () => {
  if (!requireLogin()) return
  await loadRecords()
}

initLogin()
loadCommon().then(() => syncActTimeDialog())
loadGlobalProgress()

watch(
  () => activityPhase.value,
  () => syncActTimeDialog(),
)

watch(
  () => [isLoggedIn.value, accountId(), canClaim.value],
  async ([loggedIn, id]) => {
    if (!loggedIn || !id) return
    if (actUserInfo.value?.uid) ta.login(actUserInfo.value.uid)
    await initData()
    await tryAutoClaimFirstLogin()
  },
  { immediate: true },
)

onMounted(() => {
  document.addEventListener('visibilitychange', handleVisibilityChange)
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange)
  if (shareTimer) clearTimeout(shareTimer)
})
</script>

<style scoped>
.hw-page {
  min-height: 100vh;
  display: flex;
  justify-content: center;
  background: #120b18;
  color: #f6efe4;
  font-size: 28px;
}
.hw-inner {
  width: 750px;
  min-height: 3270px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background: url('~@/views/act/halloween/assets/img/bg.png') no-repeat center top / 750px 3270px;
}
.hw-top,
.hw-row,
.hw-code-row,
.hw-dialog-actions {
  display: flex;
  gap: 24px;
  align-items: center;
  justify-content: space-between;
}
.hw-top {
  align-items: flex-start;
}
.hw-kicker {
  margin: 0;
  color: #ffb15a;
  letter-spacing: 1px;
}
h1,
h2,
h3,
p {
  margin: 0;
}
h1 {
  font-size: 56px;
}
h2 {
  margin-bottom: 24px;
  font-size: 36px;
}
.hw-phase {
  color: #d7c7b4;
}
.hw-code-row,
.hw-dialog-actions {
  display: flex;
  gap: 8px;
}
.hw-dialog {
  margin-top: 24px;
  padding: 24px;
  background: #2a1838;
  border: 1px solid #5a3b22;
  border-radius: 16px;
}
.hw-hero p + p {
  margin-top: 8px;
}
.hw-badge {
  color: #ffd36a;
}
.hw-btn,
input {
  border: 0;
  border-radius: 8px;
  font-size: 28px;
}
.hw-btn {
  padding: 16px 24px;
  background: #ff8a2a;
  color: #1b1028;
  cursor: pointer;
}
.hw-btn:disabled {
  cursor: not-allowed;
  opacity: 0.45;
}
.hw-btn--ghost {
  background: transparent;
  color: #f6efe4;
  border: 1px solid #d7c7b4;
}
.hw-btn--primary {
  min-width: 140px;
  margin-top: 12px;
}
input {
  background: #120b18;
  color: #f6efe4;
}
input {
  width: 100%;
  padding: 8px 10px;
  border: 1px solid #5a3b22;
}
.hw-mask {
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: rgba(0, 0, 0, 0.65);
}
.hw-dialog {
  width: 640px;
}
.hw-dialog label {
  display: block;
  margin: 12px 0 6px;
}
.hw-dialog ul {
  margin: 24px 0;
  padding-left: 36px;
}
</style>
